/**
 * Story 052 — useSessions + useMfaFactors hooks.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import { authFetch } from "./authFetch.js";
import { accountKeys } from "./keys.js";
import type { MfaFactorsResponse, SessionsResponse } from "./types.js";

function authReady(): boolean {
  const state = useAuthStore.getState();
  return (
    !!state.session &&
    !!state.tenant &&
    state.session.tenant.tenantSlug === state.tenant.tenantSlug
  );
}

export function useSessions(): {
  data: SessionsResponse | undefined;
  isPending: boolean;
} {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const enabled =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;
  const q = useQuery<SessionsResponse>({
    queryKey: accountKeys.sessions(),
    queryFn: () => authFetch<SessionsResponse>("/me/sessions"),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
  return { data: q.data, isPending: q.isPending };
}

export function useMfaFactors(): {
  data: MfaFactorsResponse | undefined;
  isPending: boolean;
} {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const enabled =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;
  const q = useQuery<MfaFactorsResponse>({
    queryKey: accountKeys.mfaFactors(),
    queryFn: () => authFetch<MfaFactorsResponse>("/me/mfa/factors"),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
  return { data: q.data, isPending: q.isPending };
}

// Suppress unused warning — exposed for completeness; not used elsewhere.
void authReady;
